import type { PenSubpathRange } from '@/lib/elements/types/pen-subpath-range'

/**
 * Index of the subpath range containing `pointIndex` — a compound path (multiple disjoint
 * contours, e.g. a glyph's inner counter or several glyphs merged into one shape) needs every
 * "is this closed / what's the wraparound neighbor" question scoped to ONE subpath, never the
 * whole flat `points` array.
 */
export function subpathIndexAt(subpaths: PenSubpathRange[], pointIndex: number): number {
  for (let s = 0; s < subpaths.length; s++) {
    if (pointIndex >= subpaths[s].start && pointIndex < subpaths[s].end) return s
  }

  return -1
}

/** Global index `delta` steps after `globalIndex`, wrapping within `sp`'s own range only. */
export function subpathWrapIndex(sp: PenSubpathRange, globalIndex: number, delta: number): number {
  const n = sp.end - sp.start

  return sp.start + ((((globalIndex - sp.start + delta) % n) + n) % n)
}

/** Subpath ranges after inserting one point at `insertAtIndex` within `subpathIdx`. */
export function shiftSubpathsAfterInsert(
  subpaths: PenSubpathRange[],
  subpathIdx: number,
  insertAtIndex: number
): PenSubpathRange[] {
  return subpaths.map((sp, idx) => {
    if (idx === subpathIdx) return { ...sp, end: sp.end + 1 }
    if (sp.start >= insertAtIndex) return { ...sp, start: sp.start + 1, end: sp.end + 1 }

    return sp
  })
}

/**
 * Subpath ranges after removing the point at `removeIndex` within `subpathIdx`. When the
 * containing subpath was closed with exactly 3 points, it degrades to an open 2-point line
 * (`convertToOpenLine`) — mirrors the pre-existing single-subpath removal rule, now scoped per
 * subpath instead of to the whole shape.
 */
export function shiftSubpathsAfterRemove(
  subpaths: PenSubpathRange[],
  subpathIdx: number,
  removeIndex: number,
  convertToOpenLine: boolean
): PenSubpathRange[] {
  return subpaths.map((sp, idx) => {
    if (idx === subpathIdx) {
      return { ...sp, end: sp.end - 1, isClosed: convertToOpenLine ? false : sp.isClosed }
    }

    if (sp.start > removeIndex) return { ...sp, start: sp.start - 1, end: sp.end - 1 }

    return sp
  })
}

/** Which end of its (open) subpath an endpoint anchor is — the end to continue drawing from. */
export function resolveContinueEnd(
  subpaths: PenSubpathRange[],
  endpointIndex: number
): 'first' | 'last' {
  const sp = subpaths[subpathIndexAt(subpaths, endpointIndex)]

  return sp && endpointIndex === sp.start ? 'first' : 'last'
}
